import { inject, Injector, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { countryCode } from '../../models/countryCode.model';
import Swal from 'sweetalert2';
import { CommonResponse } from '../../models/common.model';
import { responseStatus } from '../../core/constants/responseStatus.const';
import { AuthService } from '../../core/services/auth.service';
import { SpinnerService } from '../../core/services/spinner.service';
import { ToasterService } from '../../core/services/toastr.service';
import { DeleteAlertService } from '../../core/services/deleteAlert.service';
import { environment } from '../../../environments/environment.development';

export class BaseClass {
  public route: ActivatedRoute;
  public formBuilder: FormBuilder;
  public _spinnerService: SpinnerService;
  public router: Router;
  public deleteAlert: DeleteAlertService;
  public _toasterService: ToasterService;
  public _translateService: TranslateService;
  public authService: AuthService;
  public platFormId = inject(PLATFORM_ID);

  constructor(injector: Injector) {
    this.route = injector.get(ActivatedRoute);
    this.formBuilder = injector.get(FormBuilder);
    this.deleteAlert = injector.get(DeleteAlertService);
    this._spinnerService = injector.get(SpinnerService);
    this.router = injector.get(Router);
    this._toasterService = injector.get(ToasterService);
    this._translateService = injector.get(TranslateService);
    this.authService = injector.get(AuthService);
  }
  // Returns the form data with the Phone Number , DialCode handling
  processPhoneNumber(
    formGroup: FormGroup,
    phoneFieldName: string,
    dialCodeFieldName: string,
    countryCodeObject: countryCode
  ): any {
    const value = { ...formGroup.value };
    const patchValueToCountryCode = (): void => {
      let tempCountryCode = formGroup.get(phoneFieldName)?.value || '';
      if (tempCountryCode) {
        tempCountryCode = tempCountryCode.split('');
        tempCountryCode.shift();
        tempCountryCode = tempCountryCode.join('');
      }
      if (tempCountryCode !== countryCodeObject.dialCode) {
        formGroup.patchValue({
          dialCodeFieldName: `+${countryCodeObject.dialCode}`
        });
        value[dialCodeFieldName] = `+${countryCodeObject.dialCode}`;
      }
    };
    const patchValueToPhoneField = (): void => {
      let phone = formGroup.get(phoneFieldName)?.value || '';
      if (phone && phone.includes(value[dialCodeFieldName])) {
        phone = phone.split(value[dialCodeFieldName])[1]?.trim() || '';
        value[phoneFieldName] = phone;
      }
    };
    patchValueToCountryCode();
    patchValueToPhoneField();
    return value;
  }

  mapCountryFlagToMatInput(
    phoneField: any,
    country_code: string | number
  ): any {
    country_code =
      typeof country_code === 'number' ? country_code.toString() : country_code;
    let countryCode: string | string[] = country_code.split('');
    countryCode.shift();
    countryCode = countryCode.join('');
    const countryCodeObject = phoneField['allCountries'].find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (el: any) => el['dialCode'] === countryCode
    );

    phoneField['selectedCountry'] = countryCodeObject;
    return {
      phoneField,
      countryCodeObject
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logoutAlert(): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: any) => {
      Swal.fire({
        title: this._translateService.instant('AreYouSure'),
        text: this._translateService.instant('Youwillbeloggedout'),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: this._translateService.instant('Cancel'),
        confirmButtonColor: '#fdeda1',
        cancelButtonColor: '#000',
        customClass: {
          confirmButton: 'custom-confirm-btn'
        },
        confirmButtonText: this._translateService.instant('Logout')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        return resolve(result);
      });
    });
  }

  logout(): void {
    this.logoutAlert().then((res: any) => {
      if (
        Object.prototype.hasOwnProperty.call(res, 'value') &&
        res.isConfirmed === true
      ) {
        this._spinnerService.addToLoader('logout');
        this.authService.logout().subscribe({
          next: (res: CommonResponse) => {
            this._spinnerService.removeFromLoader('logout');
            if (res.statusCode === responseStatus.SUCCESS) {
              sessionStorage.removeItem(environment.storageKey);
              this.authService.profileDetailsSubject.next(null);
              this._toasterService.successToastr('LoggedOutSuccessfully');
              this.router.navigate(['/auth/welcome-back']);
            }
          },
          error: () => {
            this._spinnerService.removeFromLoader('logout');
          }
        });
      }
    });
  }

  deleteAccount(): void {
    this.deleteAlert.delete().then((res: any) => {
      if (
        Object.prototype.hasOwnProperty.call(res, 'value') &&
        res.isConfirmed === true
      ) {
        // Call the service or API that returns an Observable
        this.authService.deleteAccount().subscribe({
          next: (res: CommonResponse) => {
            this._spinnerService.removeFromLoader('delete');
            if (res) {
              this._toasterService.successToastr('AccountDeleteSuccessfully');
              sessionStorage.removeItem(environment.storageKey);
              this.authService.profileDetailsSubject.next(null);
              this.router.navigate(['/auth/welcome-back']);
            }
          },
          error: () => {
            this._spinnerService.removeFromLoader('delete');
          }
        });
      }
    });
  }
}
